<template>
  <div class="h-full flex flex-col">
    <div v-if="!hasData" class="flex items-center justify-center h-full">
      <p class="text-sm text-gray-600">ไม่มีข้อมูล</p>
    </div>

    <PieChart
      v-else-if="variant === 'pie'"
      :chart-id="chartId"
      :series="series"
      :chart-label="labels"
      :height="height"
    />
    <DoughnutChart
      v-else-if="variant === 'doughnut'"
      :chart-id="chartId"
      :series="series"
      :chart-label="labels"
      :height="height"
      :unit="unit"
      :cutout="cutout"
      :show-total="showTotal"
      :is-show-percent="isShowPercent"
      :legend-layout="legendLayout"
      :palette="palette"
    />
    <template v-else>
      <div class="relative flex-1 min-h-0">
        <canvas ref="canvasRef" :id="chartId" data-chart-export class="!h-full !w-full" />
      </div>
      <div class="flex justify-center flex-none">
        <ul
          ref="legendRef"
          class="mt-4"
          :class="legendLayout === 'horizontal'
            ? 'flex flex-wrap gap-x-5 gap-y-1.5 justify-center'
            : 'grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5'"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch, nextTick } from "vue";
import Chart from "chart.js/auto";
import { Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
Tooltip.positioners.mouse = (chartElements, eventPosition) => eventPosition;
import { globalNumDecFixed } from "@/utils/format-number.js";
import DoughnutChart from "@/components/doughnut-chart/Main.vue";
import PieChart from "@/components/pie-chart/Main.vue";

// const PALETTE = ["#3A506A", "#F4D35E", "#11D67A", "#20A0D7", "#AF52DE", "#606468", "#F73C4B"];

const props = defineProps({
  variant: { type: String, default: "plain" }, // 'plain' | 'doughnut' | 'pie'
  chartId: { type: String, required: true },
  series: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  unit: { type: String, default: "รายการ" },
  height: { type: Number, default: 200 },
  isShowPercent: { type: Boolean, default: false },
  legendLayout: { type: String, default: "vertical" }, // 'vertical' | 'horizontal'
  cutout: { type: String, default: '60%' },
  showTotal: { type: Boolean, default: true },
  showDataLabel: { type: Boolean, default: false },
  palette: { type: Array, default: () => ["#3A506A", "#F4D35E", "#11D67A", "#20A0D7", "#AF52DE", "#606468", "#F73C4B"] },
  minLabelPct: { type: Number, default: 3 }, // hide datalabel when slice < this % of total
});

const total = computed(() => props.series.reduce((a, v) => a + v, 0));

const withOpacity = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const hasData = computed(() => props.series?.length > 0 && props.series.some((v) => v > 0));

const _measureCanvas = document.createElement('canvas').getContext('2d');
const wrapText = (text, maxPx = 230, font = '11px Kanit, sans-serif') => {
  if (!text) return [text];
  _measureCanvas.font = font;
  if (_measureCanvas.measureText(text).width <= maxPx) return [text];
  const lines = [];
  let current = '';
  for (const char of text) {
    const test = current + char;
    if (_measureCanvas.measureText(test).width > maxPx) {
      if (current) lines.push(current);
      current = char;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const doughnutData = computed(() => ({
  labels: props.labels,
  datasets: [
    {
      data: props.series,
      backgroundColor: props.palette,
      hoverBackgroundColor: props.palette.map(c => withOpacity(c, 0.5)),
      borderWidth: 4,
      borderColor: "#fff",
    },
  ],
}));

const canvasRef = ref(null);
const legendRef = ref(null);

const buildLegend = (chart) => {
  const ul = legendRef.value;
  if (!ul) return;
  ul.innerHTML = "";

  chart.legend.legendItems.forEach((item, index) => {
    if (index >= props.series.length || index >= props.labels.length) return;

    const li = document.createElement("li");
    li.className = "flex items-center gap-2 text-xs cursor-pointer select-none";

    const dot = document.createElement("span");
    dot.className = "w-2.5 h-2.5 rounded-full flex-none transition-opacity";
    dot.style.backgroundColor = item.fillStyle;

    const label = document.createElement("span");
    label.className = "text-gray-600 truncate";
    label.title = item.text;
    label.textContent = item.text;

    li.appendChild(dot);
    li.appendChild(label);

    if (props.isShowPercent) {
      const pctEl = document.createElement("span");
      pctEl.className = "ml-1 text-gray-400 flex-none";
      if (total.value) {
        let pct = (props.series[index] * 100) / total.value;
        pct = Math.round(pct) === pct ? pct : pct.toFixed(1);
        pctEl.textContent = pct + "%";
      } else {
        pctEl.textContent = "0%";
      }
      li.appendChild(pctEl);
    }

    li.onclick = () => {
      chart.toggleDataVisibility(index);
      li.classList.toggle("opacity-40");
      chart.update();
    };

    ul.appendChild(li);
  });
};

let chartInstance = null;

const init = () => {
  if (chartInstance) return;
  const canvas = canvasRef.value?.getContext("2d");
  if (!canvas) return;

  Chart.defaults.font.family = "Kanit";

  const chart = new Chart(canvas, {
    type: "doughnut",
    data: doughnutData.value,
    plugins: props.showDataLabel ? [ChartDataLabels] : [],
    options: {
      cutout: "30%",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          position: "mouse",
          maxWidth: 250,
          callbacks: {
            label: (ctx) => {
              const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
              if (!sum) return "";
              let pct = (ctx.parsed * 100) / sum;
              pct = Math.round(pct) === pct ? pct : pct.toFixed(1);
              return [...wrapText(ctx.label), `${pct}% (${globalNumDecFixed(ctx.parsed)} ${props.unit})`];
            },
          },
        },
        datalabels: props.showDataLabel ? {
          display: (ctx) => {
            const val = ctx.dataset.data[ctx.dataIndex];
            if (!val) return false;
            const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
            if (!sum) return false;
            return (val / sum) * 100 >= props.minLabelPct;
          },
          color: "#fff",
          textAlign: "center",
          font: { size: 12, family: "Kanit", weight: "normal" },
          formatter: (value, ctx) => {
            const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
            if (!sum) return "";
            let pct = (value * 100) / sum;
            pct = Math.round(pct) === pct ? pct : pct.toFixed(1);
            return `${pct}%\n(${value.toLocaleString()}\n${props.unit})`;
          },
        } : { display: false },
      },
    },
  });

  buildLegend(chart);

  canvasRef.value.instance = chart;
  chartInstance = chart;
};

watch(doughnutData, () => {
  if (!chartInstance) return;
  chartInstance.data = doughnutData.value;
  chartInstance.update();
  buildLegend(chartInstance);
});

watch(hasData, async (val) => {
  if (!val) {
    chartInstance = null;
    return;
  }
  if (props.variant !== "plain") return;
  await nextTick();
  init();
});

onMounted(() => {
  if (props.variant === "plain" && hasData.value) init();
});
</script>
